import { useCallback, useMemo, useReducer, useRef } from 'react';

import Cookies from 'js-cookie';

import { TailwindWrapper } from '@utils/TailwindWrapper';
import { Button, Dialog, Text } from '@ui-component-library';

import { usePropsDataStore } from '../../store/PropsDataStore';

import SellTimberStep1 from './SellTimberStep1';
import SellTimberStep2 from './SellTimberStep2';
import SellTimberStep3 from './SellTimberStep3';
import SellTimberStep4 from './SellTimberStep4';
import SellTimberGrotStep2 from './SellTimberGrotStep2';
import SellTimberGrotStep3 from './SellTimberGrotStep3';
import SellTimberGrotStep4 from './SellTimberGrotStep4';
import { fetchAdForSellTimber, normalizeGeometryForWizard } from './helpers/api';
import { isGrotArrangement, SELL_TIMBER_GENERIC_ERROR } from './helpers/sellTimberShared';

const SELL_TIMBER_DEFAULT_STEP_COMPONENTS = [
  SellTimberStep1,
  SellTimberStep2,
  SellTimberStep3,
  SellTimberStep4
];

function resolveSellTimberStepComponent(wizard) {
  const { step } = wizard;
  if (step !== 1 && isGrotArrangement(wizard.adCreateData?.selectedAction)) {
    if (step === 2) return SellTimberGrotStep2;
    if (step === 3) return SellTimberGrotStep3;
    if (step === 4) return SellTimberGrotStep4;
    return SellTimberGrotStep2;
  }
  return SELL_TIMBER_DEFAULT_STEP_COMPONENTS[step - 1] ?? SellTimberStep1;
}

/**
 * Wizard state aligned with backend Ad: `adCreateData`, `geometry`, `forestWithSections` ({ sections }), etc.
 * @see backend/vb-django/apps/main/models/ad.py
 */
const initialWizardState = {
  step: 1,
  adCreateData: null,
  geometry: null,
  forestWithSections: { sections: [] },
  editingAdId: null,
  initialLoad: { status: 'idle', error: null },
  /** Fastighetsdata + avverkningsanmälningar for step 2 map; survives step changes; keyed by property ref. */
  mapPropertyCache: null
};

/**
 * @param {number | null | undefined} initialAdId
 */
function createInitialWizardState(initialAdId) {
  return {
    ...initialWizardState,
    initialLoad: {
      status: initialAdId != null ? 'loading' : 'idle',
      error: null
    }
  };
}

function wizardReducer(state, action) {
  switch (action.type) {
    case 'INITIAL_LOAD_IDLE':
      return { ...state, initialLoad: { status: 'idle', error: null } };
    case 'INITIAL_LOAD_LOADING':
      return { ...state, initialLoad: { status: 'loading', error: null } };
    case 'INITIAL_LOAD_ERROR':
      return { ...state, initialLoad: { status: 'error', error: action.error } };
    case 'HYDRATE_DRAFT':
      return {
        ...state,
        adCreateData: action.adCreateData,
        geometry: normalizeGeometryForWizard(action.geometry),
        forestWithSections: action.forestWithSections,
        editingAdId: action.editingAdId,
        step: 1,
        initialLoad: { status: 'ready', error: null },
        mapPropertyCache: null
      };
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'SET_AD_CORE': {
      const payload = action.payload;
      const nextRef = payload?.selectedProperty?.ref;
      let mapPropertyCache = state.mapPropertyCache;
      if (mapPropertyCache != null && String(mapPropertyCache.ref) !== String(nextRef ?? '')) {
        mapPropertyCache = null;
      }
      return { ...state, adCreateData: payload, mapPropertyCache };
    }
    case 'MAP_PROPERTY_CACHE_LOADING':
      return {
        ...state,
        mapPropertyCache: {
          ref: String(action.ref),
          sections: [],
          borderCoords: null,
          propertyGeojson: null,
          fellingApplicationsGeojson: null,
          propertyFetch: { status: 'loading', error: null },
          fellingFetch: { status: 'idle', error: null }
        }
      };
    case 'MAP_PROPERTY_CACHE_SET': {
      const p = action.payload;
      const cur = state.mapPropertyCache;
      /** Drop responses for a previous property (e.g. prefetch cancelled on step change, stale network). */
      if (cur != null && String(cur.ref) !== String(p.ref)) {
        return state;
      }
      return {
        ...state,
        mapPropertyCache: {
          ref: String(p.ref),
          sections: p.sections ?? [],
          borderCoords: p.borderCoords ?? null,
          propertyGeojson: p.propertyGeojson ?? null,
          fellingApplicationsGeojson: p.fellingApplicationsGeojson ?? null,
          propertyFetch: p.propertyFetch,
          fellingFetch: p.fellingFetch
        }
      };
    }
    case 'SET_GEOMETRY':
      return { ...state, geometry: normalizeGeometryForWizard(action.payload) };
    case 'SET_FOREST_WITH_SECTIONS': {
      const next = action.payload;
      const sections =
        typeof next === 'function' ? next(state.forestWithSections.sections) : next;
      return { ...state, forestWithSections: { ...state.forestWithSections, sections } };
    }
    case 'RESET_SELL_TIMBER_FLOW':
      return {
        ...initialWizardState,
        step: 1,
        initialLoad: { status: 'idle', error: null },
        editingAdId: null
      };
    default:
      return state;
  }
}

/**
 * @param {{ initialAdId?: number | null }} props
 */
export default function SellTimber({ initialAdId = null }) {
  const [wizard, dispatch] = useReducer(wizardReducer, initialAdId, createInitialWizardState);

  const activeProps = usePropsDataStore((s) => s.activeProps);
  const setProps = usePropsDataStore((s) => s.setProps);

  const lockedRefStr =
    wizard.adCreateData?.selectedProperty?.ref != null
      ? String(wizard.adCreateData.selectedProperty.ref)
      : null;
  const storeRefStr =
    activeProps.length === 1 ? String(activeProps[0].ref) : null;

  const propertyMismatchWithFlow = useMemo(() => {
    if (lockedRefStr == null) return false;
    if (storeRefStr == null) return true;
    return storeRefStr !== lockedRefStr;
  }, [lockedRefStr, storeRefStr]);

  const lockedDesignation = wizard.adCreateData?.selectedProperty?.designation;

  const handleRevertPropertySelection = useCallback(() => {
    if (lockedRefStr == null) return;
    const allProps = usePropsDataStore.getState().props;
    const nextProps = allProps.map((p) => {
      const isLocked = String(p.ref) === String(lockedRefStr);
      return { ...p, active: isLocked, checked: isLocked };
    });
    setProps(nextProps);
    /** Same as PropertySelector + MinSkog bootstrap: cookie refs are merged into active/checked on reload. */
    const activeRefs = nextProps
      .filter((p) => p.active)
      .filter((p) => (p.data?.sections?.length ?? 0) <= 300)
      .map((p) => p.ref)
      .join(',');
    Cookies.set('activeProps', activeRefs);
  }, [lockedRefStr, setProps]);

  const handleResetFlowForNewProperty = useCallback(() => {
    dispatch({ type: 'RESET_SELL_TIMBER_FLOW' });
  }, [dispatch]);

  const latestInitialAdIdRef = useRef(initialAdId);
  latestInitialAdIdRef.current = initialAdId;

  const loadStartedForIdRef = useRef(null);
  const prevInitialAdIdRef = useRef(initialAdId);

  if (initialAdId != null && loadStartedForIdRef.current !== initialAdId) {
    loadStartedForIdRef.current = initialAdId;
    const id = initialAdId;
    // Defer dispatch + fetch until after this render finishes (must not update state during render).
    queueMicrotask(() => {
      if (latestInitialAdIdRef.current !== id) return;
      dispatch({ type: 'INITIAL_LOAD_LOADING' });
      void fetchAdForSellTimber(id).then((res) => {
        if (latestInitialAdIdRef.current !== id) return;
        if (!res.ok) {
          dispatch({
            type: 'INITIAL_LOAD_ERROR',
            error: SELL_TIMBER_GENERIC_ERROR
          });
          return;
        }
        dispatch({
          type: 'HYDRATE_DRAFT',
          adCreateData: res.adCreateData,
          geometry: res.geometry,
          forestWithSections: res.forestWithSections,
          editingAdId: res.editingAdId
        });
      });
    });
  } else if (initialAdId == null) {
    loadStartedForIdRef.current = null;
  }

  if (initialAdId == null && prevInitialAdIdRef.current != null) {
    // Same: defer idle dispatch until after render.
    queueMicrotask(() => dispatch({ type: 'INITIAL_LOAD_IDLE' }));
  }
  prevInitialAdIdRef.current = initialAdId;

  const stepProps = { wizard, dispatch };
  const StepComponent = resolveSellTimberStepComponent(wizard);

  const inner =
    wizard.initialLoad.status === 'loading' ? (
      <TailwindWrapper>
        <div className="mx-auto flex max-w-lg flex-col gap-2 p-8">
          <Text variant="text-base/leading-normal/normal" className="text-charcoal-700">
            Laddar annonsutkast…
          </Text>
        </div>
      </TailwindWrapper>
    ) : wizard.initialLoad.status === 'error' && initialAdId != null ? (
      <TailwindWrapper>
        <div className="mx-auto flex max-w-lg flex-col gap-4 p-8">
          <Text variant="text-base/leading-normal/normal" className="text-red-700" role="alert">
            {wizard.initialLoad.error ?? SELL_TIMBER_GENERIC_ERROR}
          </Text>
          <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
            Gå tillbaka och försök igen, eller öppna Sälj virke utan att ladda ett utkast.
          </Text>
        </div>
      </TailwindWrapper>
    ) : (
      <StepComponent {...stepProps} />
    );

  return (
    <>
    {inner}
    <TailwindWrapper>
      
      <Dialog
        isOpen={propertyMismatchWithFlow}
        onClose={handleRevertPropertySelection}
        size="small"
      >
        <Dialog.Header>
          <Text variant="text-lg/leading-normal/medium">Byta fastighet?</Text>
        </Dialog.Header>
        <Dialog.Content>
          <Text variant="text-sm/leading-normal/normal" className="text-charcoal-700">
            Du har börjat fylla i annonsen för{' '}
            {lockedDesignation ? (
              <span className="capitalize">{String(lockedDesignation)}</span>
            ) : (
              'den valda fastigheten'
            )}
            . Om du väljer en annan fastighet i fastighetsväljaren nollställs allt du gjort i detta
            flöde (karta, avdelningsuppgifter med mera).
          </Text>
        </Dialog.Content>
        <Dialog.Footer>
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="tertiary" onClick={handleRevertPropertySelection}>
              Behåll nuvarande fastighet
            </Button>
            <Button type="button" variant="primary" onClick={handleResetFlowForNewProperty}>
              Nollställ och fortsätt med ny fastighet
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog>
    </TailwindWrapper>
    </>
  );
}
